/**
 * Biometric authentication helper.
 *
 * Wraps @capgo/capacitor-native-biometric — Capacitor 7 compatible,
 * Android BiometricPrompt + iOS Face/Touch ID. Loaded dynamically so the
 * plugin isn't pulled into the web bundle.
 *
 * Opt-in flow: user logs in with password → we ask if they want to enable
 * biometric unlock → on next cold-start the login screen surfaces a
 * "Unlock with Fingerprint" button.
 */
import { Capacitor } from '@capacitor/core';

// Mirrors BiometryType enum (definitions.d.ts) so we don't need the import.
const BIOMETRY_LABEL = {
  0: 'Biometric',     // NONE
  1: 'Touch ID',      // TOUCH_ID
  2: 'Face ID',       // FACE_ID
  3: 'Fingerprint',   // FINGERPRINT (Android)
  4: 'Face',          // FACE_AUTHENTICATION (Android)
  5: 'Iris',          // IRIS_AUTHENTICATION
  6: 'Biometric',     // MULTIPLE
};

let _plugin = null;
async function loadPlugin() {
  if (_plugin) return _plugin;
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const mod = await import('@capgo/capacitor-native-biometric');
    _plugin = mod;
    return mod;
  } catch (e) {
    console.warn('Biometric plugin not available:', e?.message);
    return null;
  }
}

/**
 * Returns: { available, type, reason }
 */
export async function checkBiometric() {
  const mod = await loadPlugin();
  if (!mod) return { available: false, type: 'Biometric', reason: 'Not a mobile build' };
  try {
    const result = await mod.NativeBiometric.isAvailable();
    return {
      available: !!result.isAvailable,
      type: BIOMETRY_LABEL[result.biometryType] || 'Biometric',
      reason: result.errorCode,
    };
  } catch (e) {
    return { available: false, type: 'Biometric', reason: e?.message };
  }
}

/**
 * Prompts the OS biometric sheet. Resolves on success, throws on cancel/failure.
 */
export async function promptBiometric(reason = 'Unlock Ellora EPC ERP') {
  const mod = await loadPlugin();
  if (!mod) throw new Error('Biometric not available on this platform');

  // Crash fix: `useFallback: true` triggers IllegalArgumentException on Android 10/11
  // (API 29/30) with @capgo/capacitor-native-biometric < 7.1.0 when BiometricManager
  // doesn't support credential fallback. Gate it behind Android SDK version check.
  let useFallback = true;
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    // useFallback is safe on Android 12+ (API 31) and all iOS — avoid it on older Android
    if (info?.platform === 'android') {
      const sdkStr = info?.androidSDKVersion ?? info?.osVersion ?? '30';
      const sdk = parseInt(String(sdkStr), 10);
      useFallback = !isNaN(sdk) && sdk >= 31;
    }
  } catch {
    useFallback = false; // can't determine — default safe
  }

  await mod.NativeBiometric.verifyIdentity({
    reason,
    title: 'Unlock Ellora ERP',
    subtitle: reason,
    description: 'Use your fingerprint or face to continue.',
    useFallback,
    negativeButtonText: 'Cancel',
  });
  return true;
}

// Server key under which credentials are stored in the OS keystore/keychain.
const CRED_SERVER = 'ellora-epc-erp';

/**
 * Securely store the user's credentials in the hardware-backed OS keystore
 * (Android Keystore / iOS Keychain). Used so the user can re-login via
 * biometric AFTER an explicit logout (when no session token exists).
 */
export async function setBiometricCredentials(username, password) {
  const mod = await loadPlugin();
  if (!mod) throw new Error('Biometric not available on this platform');
  await mod.NativeBiometric.setCredentials({ username, password, server: CRED_SERVER });
  return true;
}

/**
 * Retrieve the stored credentials. MUST be called only after a successful
 * promptBiometric(). Returns { username, password } or null if none stored.
 */
export async function getBiometricCredentials() {
  const mod = await loadPlugin();
  if (!mod) return null;
  try {
    const creds = await mod.NativeBiometric.getCredentials({ server: CRED_SERVER });
    if (creds && creds.username && creds.password) return creds;
    return null;
  } catch {
    return null;
  }
}

/**
 * Remove stored credentials (e.g. on "disable biometric" or stale-password 401).
 */
export async function deleteBiometricCredentials() {
  const mod = await loadPlugin();
  if (!mod) return;
  try { await mod.NativeBiometric.deleteCredentials({ server: CRED_SERVER }); } catch { /* ignore */ }
}
